import { useQuery, useMutation } from '@tanstack/react-query';
import { showToast } from '@repo/ui';
import { imageAssetsApi } from '../api';

/**
 * Query key factory for image assets
 */
export const imageQueryKeys = {
  all: ['image-assets'] as const,
};

/**
 * Hook to fetch image assets
 * @returns React Query result with image assets
 */
export function useImageAssets() {
  return useQuery({
    queryKey: imageQueryKeys.all,
    queryFn: () => imageAssetsApi.getImages(),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook for image upload mutation
 * @param onSuccessCallback - Optional callback to execute on successful upload
 * @returns Mutation hook for uploading images
 */
export function useUploadImage(onSuccessCallback?: () => void) {
  return useMutation({
    mutationFn: async (formData: FormData) => {
      return imageAssetsApi.uploadImage(formData);
    },
    onSuccess: () => {
      showToast.success('Image uploaded successfully!');
      onSuccessCallback?.();
    },
    onError: (error: any) => {
      showToast.error(`Image upload failed: ${error.message}`);
    },
  });
}

/**
 * Hook for image deletion mutation
 * @param onSuccessCallback - Optional callback to execute on successful deletion
 * @returns Mutation hook for deleting images
 */
export function useDeleteImage(onSuccessCallback?: () => void) {
  return useMutation({
    mutationFn: async (imageId: string) => {
      return imageAssetsApi.deleteImage(imageId);
    },
    onSuccess: () => {
      showToast.success('Image deleted successfully!');
      onSuccessCallback?.();
    },
    onError: (error: any) => {
      showToast.error(`Delete failed: ${error.message}`);
    },
  });
}

